import React, { useEffect, useState, useContext } from 'react';
import { GatsbyLinkProps, Link, navigate } from 'gatsby';
import styled, { ThemeContext } from 'styled-components';
import {
  animated,
  useSpring,
  config,
} from 'react-spring';
import Button from '../Button';
import SvgMenuArrowIcon from '../fromSvg/SvgMenuArrowIcon';


const StyledBurgerMenu = styled.div`
  position: fixed;
  top: 25px;
  right: 40px;
  height: 30px;
  width: 30px;
  cursor: pointer;
`;

const StyledMenu = styled(animated.div)`
  top: 72px;
  right: 0;
  position: fixed;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  z-index: 1000;
  padding: 40px 20px;
`;


const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.fontSize.small};
  text-transform: uppercase;
  text-decoration: none;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  margin: 20px 0;
  @media(min-width: ${({ theme }) => theme.screenSizeWidth.medium}){
    font-size: ${({ theme }) => theme.fontSize.medium};
  }
  &:hover {
    color: ${({ theme }) => theme.colors.orange};
  }
`;

const MenuItemContainer = styled.div<{ isMenuOpened: boolean }>`
  border-bottom: 2px solid ${({ theme, isMenuOpened }) => (isMenuOpened ? theme.colors.dark : theme.colors.white)};
  display: flex;
  align-items: center;
  & > * {
    margin-left: 5px;
    margin-right: 5px;
  }
  transition: border-bottom 400ms ease-in-out;
`;

const StyledMenuArrowIcon = styled(SvgMenuArrowIcon)<{ isMenuOpened: boolean }>`
  transform: ${({ isMenuOpened }) => (isMenuOpened ? 'rotate(0)' : 'rotate(180deg)')};
  transition: transform 400ms ease-in-out;
  cursor: pointer;
`;

const OfferMenuContainer = styled.div<{ isMenuOpened: boolean }>`
  flex-direction: column;
  display: flex;
  align-items: center;
  & > * {
    color: ${({ theme }) => theme.colors.grayWithOpacity};
  }
  opacity: ${({ isMenuOpened }) => (isMenuOpened ? 1 : 0)};
  max-height: ${({ isMenuOpened }) => (isMenuOpened ? 300 : 0)}px;
  transition: all 400ms ease-in-out;
  overflow: hidden;
`;

const MenuItem = ({ ...props }: GatsbyLinkProps<{}>) => {
  const theme = useContext(ThemeContext);
  return (
    <>
      { /* @ts-ignore */}
      <StyledLink
        activeStyle={{ color: `${theme.colors.orange}`, borderBottom: `2px solid ${theme.colors.orange}` }}
        {...props}
      />
    </>
  );
};

function BurgerMenu() {
  const theme = useContext(ThemeContext);
  const [isOpened, setIsOpened] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const openedTransformationConfig = {
    top: 'translate(2, 7) rotate(0) scale(1)',
    center: 'translate(2, 19) rotate(0) scale(1)',
    bottom: 'translate(2, 31) rotate(0) scale(1)',
    color: theme.colors.orange,
  };
  const [transformationConfig, setTransformationConfig] = useState(openedTransformationConfig);
  useEffect(
    () => {
      if (!isOpened && isHovered) {
        setTransformationConfig({
          top: 'translate(1, 3) rotate(0) scale(1.05)',
          center: 'translate(2, 19) rotate(0) scale(1)',
          bottom: 'translate(1, 35) rotate(0) scale(1.05)',
          color: theme.colors.orange,
        });
      } else if (!isOpened && !isHovered) {
        setTransformationConfig({
          top: 'translate(2, 7) rotate(0) scale(1)',
          center: 'translate(2, 19) rotate(0) scale(1)',
          bottom: 'translate(2, 31) rotate(0) scale(1)',
          color: theme.colors.orange,
        });
      } else {
        setTransformationConfig({
          top: 'translate(5, 32) rotate(-45) scale(1)',
          center: 'translate(10, 4) rotate(45) scale(1)',
          bottom: 'translate(5, 32) rotate(-45) scale(1)',
          color: theme.colors.orange,
        });
      }
    },
    [isHovered, isOpened, theme]
  );
  const burgerAnimation = useSpring({
    to: transformationConfig,
    config: config.stiff,
  });

  const burgerAnimationProps = burgerAnimation ? { ...burgerAnimation } : {
    color: '',
    top: '',
    bottom: '',
    center: '',
  };

  return (
    <>
      <StyledBurgerMenu
        role="button"
        tabIndex={0}
        onClick={
          () => { setIsOpened(!isOpened) }
        }
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        onMouseMove={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <animated.svg width="30" height="30" viewBox="0 0 44 44" fill={burgerAnimationProps.color} xmlns="http://www.w3.org/2000/svg">
          <animated.rect width="40" height="6" transform={burgerAnimationProps.top} />
          <animated.rect width="40" height="6" transform={burgerAnimationProps.center} />
          <animated.rect width="40" height="6" transform={burgerAnimationProps.bottom} />
        </animated.svg>
      </StyledBurgerMenu>
      {isOpened && (
        <StyledMenu>
          <MenuItem
            to="/o-mnie"
          >
            O mnie
          </MenuItem>
          <MenuItem
            to="/realizacje"
          >
            Realizacje
          </MenuItem>
          <MenuItem
            to="/ile-to-kosztuje"
          >
            Ile to kosztuje
          </MenuItem>
          <MenuItemContainer isMenuOpened={isMenuOpened}>
            <MenuItem
              to="/oferta"
            >
              Oferta
            </MenuItem>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsMenuOpened(!isMenuOpened)}
            >
              <StyledMenuArrowIcon
                height={13}
                isMenuOpened={isMenuOpened}
              />
            </div>
          </MenuItemContainer>
          <OfferMenuContainer isMenuOpened={isMenuOpened}>
            <MenuItem
              to="/lazienka"
            >
              Łazienka
            </MenuItem>
            <MenuItem
              to="/kuchnia"
            >
              Kuchnia
            </MenuItem>
            <MenuItem
              to="/pokoj"
            >
              Pokój
            </MenuItem>
            <MenuItem
              to="/kompleksowy-remont"
            >
              Wszystko
            </MenuItem>
            <MenuItem
              to="/cos-innego"
            >
              Coś innego
            </MenuItem>
          </OfferMenuContainer>
          <MenuItem
            to="/kontakt"
          >
            Kontakt
          </MenuItem>
          <Button onClick={() => navigate('/kontakt/#form')}>Skontaktuj sie</Button>
        </StyledMenu>
      )}
    </>
  );
}

export default BurgerMenu;
